import { PropsWithChildren, ReactElement, useEffect, useRef, useState } from "react"

import { CheckCircleIcon, ExclamationCircleIcon, InformationCircleIcon, XMarkIcon } from "@heroicons/react/24/solid"
import { twMerge } from "tailwind-merge"

import { flashClassName, FlashKind } from "./sharedClassNames"

export type FlashProps = {
  kind?: FlashKind
  rawHTML?: string
  className?: string
  scrollIntoView?: boolean
  icon?: () => ReactElement
  noIcon?: boolean
  notClosable?: boolean
  onClose?: () => void
  headline?: string | (() => ReactElement)
}

const iconMap: Record<FlashKind, ReactElement> = {
  success: <CheckCircleIcon className="h-5 w-5" />,
  warning: <ExclamationCircleIcon className="h-5 w-5" />,
  danger: <ExclamationCircleIcon className="h-5 w-5" />,
  error: <ExclamationCircleIcon className="h-5 w-5" />,
  info: <InformationCircleIcon className="h-5 w-5" />,
}

/**
 * Standard flash message component.
 */
export function Flash(props: PropsWithChildren<FlashProps>) {
  const {
    className,
    scrollIntoView = false,
    kind = "info",
    rawHTML,
    notClosable = false,
    onClose = null,
    noIcon = false,
    icon = null,
    headline = null,
    children,
  } = props

  const divRef = useRef<HTMLDivElement>(null)
  const [isOpen, setIsOpen] = useState(true)

  const isClosable = !notClosable
  const showIcon = !noIcon

  const handleOnClose = () => {
    if (typeof onClose === "function") {
      onClose()
    } else {
      setIsOpen(false)
    }
  }

  useEffect(() => {
    if (scrollIntoView) {
      requestAnimationFrame(() => {
        divRef.current?.scrollIntoView()
      })
    }
  }, [])

  if (rawHTML) {
    return (
      <div
        ref={divRef}
        className={twMerge(flashClassName(kind), "block scroll-m-8", className)}
        dangerouslySetInnerHTML={{ __html: rawHTML }}
      />
    )
  } else {
    return (
      <div
        ref={divRef}
        className={twMerge(
          flashClassName(kind),
          "flex scroll-m-8 flex-row justify-between text-sm",
          !isOpen && "hidden",
          className
        )}
      >
        {showIcon && <div className="mr-5">{typeof icon === "function" ? icon() : iconMap[kind]}</div>}
        <div className="grow text-left">
          {headline && (typeof headline === "function" ? headline() : <div className="mb-2 font-bold">{headline}</div>)}
          {children}
        </div>
        {isClosable && (
          <div className="ml-5">
            <button type="button" className="block cursor-pointer" onClick={handleOnClose}>
              <XMarkIcon className="h-5 w-5" />
            </button>
          </div>
        )}
      </div>
    )
  }
}
